'use client';
import styles from './action-button.module.css';
import { useState } from 'react';
import {
	faEyeSlash,
	faHeart,
} from '@awesome.me/kit-012d1b9af0/icons/classic/regular';
import {
	faEye,
	faHeart as faHeartSolid,
} from '@awesome.me/kit-012d1b9af0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useModal from '@/components/modal/use-modal';
import Modal from '@/components/modal/modal';
import Auth from '@/components/auth/auth';
import { useSession } from 'next-auth/react';
import { updateUserfollowOrHideTrial } from '@/lib/user';
import { toast } from 'sonner';
import { useUser } from '@/context/user-context';

type ActionType = 'follow' | 'hide';

export default function ActionButton({
	wrap = false,
	nctid,
}: {
	wrap?: boolean;
	nctid: string;
}) {
	const { data: session } = useSession();
	const { isOpen, onClose, onOpen } = useModal();
	const { user, setUser } = useUser();

	const [isLoading, setIsLoading] = useState(false);
	const [localFollow, setLocalFollow] = useState(user.follow.includes(nctid));
	const [localHide, setLocalHide] = useState(user.hide.includes(nctid));
	const [isFollowing, setIsFollowing] = useState(false);

	async function handleAction(type: ActionType) {
		if (!session?.user) return onOpen('login-modal');

		const previousUserState = { ...user };

		setUser((prev) => {
			const updatedSet = new Set(prev[type]);
			if (prev[type].includes(nctid)) {
				updatedSet.delete(nctid);
			} else {
				updatedSet.add(nctid);
			}
			return {
				...prev,
				[type]: Array.from(updatedSet),
			};
		});

		if (type === 'follow') {
			setIsFollowing(true);
			setLocalFollow((prev) => !prev);
		} else {
			setLocalHide((prev) => !prev);
		}

		try {
			const res = await updateUserfollowOrHideTrial(
				session.user.id,
				nctid,
				type
			);

			if (!res.success) {
				setUser(previousUserState);
				if (type === 'follow') setLocalFollow((prev) => !prev);
				else setLocalHide((prev) => !prev);

				toast.error(res.message);
			}
		} catch (error: any) {
			console.error('actionbutton', error);
			setUser(previousUserState);
			if (type === 'follow') setLocalFollow((prev) => !prev);
			else setLocalHide((prev) => !prev);

			toast.error(`An error occurred: ${error.message || 'Failed to update'}`);
		} finally {
			if (type === 'follow') setIsFollowing(false);
		}
	}

	return (
		<div className={`${styles.options} ${wrap && styles.wrap}`}>
			<div className={styles.option}>
				<button onClick={() => handleAction('hide')} disabled={isLoading}>
					{localHide ? (
						<>
							Unhide <FontAwesomeIcon icon={faEye} size='xl' />
						</>
					) : (
						<>
							Hide <FontAwesomeIcon icon={faEyeSlash} size='xl' />
						</>
					)}
				</button>
			</div>

			<div
				className={styles.option}
				style={{
					opacity: isFollowing ? 0.5 : 1,
				}}
			>
				<button onClick={() => handleAction('follow')} disabled={isLoading}>
					{localFollow ? (
						<>
							Unfollow <FontAwesomeIcon icon={faHeartSolid} size='xl' />
						</>
					) : (
						<>
							Follow <FontAwesomeIcon icon={faHeart} size='xl' />
						</>
					)}
				</button>
			</div>

			<Modal
				isOpen={isOpen('login-modal')}
				onClose={() => onClose('login-modal')}
				auto
			>
				<Auth onClose={() => onClose('login-modal')} />
			</Modal>
		</div>
	);
}
